import { useMemo } from 'react';
import { CircadianPhase, EnergyFeedbackType } from '@demind-inc/core';
import dayjs from 'dayjs';
import { useAuthContext, useCircadianContext } from '../context';
import { useUpdatePhaseEma } from '../mutations';
import { circadianPhaseInfo } from '../helpers';

 
interface UsePhaseEmaTimelineProps {
  date: string;
}

export interface PhaseEmaTimeline {
  phase: CircadianPhase;
  startTime: string;
  endTime: string;
  title: string;
  energy?: EnergyFeedbackType;
}

export const usePhaseEmaTimeline = ({ date }: UsePhaseEmaTimelineProps) => {
  const { user } = useAuthContext();
  const { phaseEmasByDate, circadianPhaseBoundariesByDate } = useCircadianContext();
  const { updatePhaseEma } = useUpdatePhaseEma();

  const phaseEmaTimeline = useMemo(() => {
    const phaseEma = phaseEmasByDate.find((p) => p.date === date);
    const phaseBoundaries = circadianPhaseBoundariesByDate.find((p) => p.date === date);
    if (!phaseBoundaries) {
      return [];
    }

    const phaseEmaTimeline: PhaseEmaTimeline[] = Object.entries(phaseBoundaries.details)
      .filter(
        ([phase, { start, end }]) =>
          !!phase && !['undefined', 'default', 'evening_low'].includes(phase) && !!start && !!end
        // Exclude wind-down phase too
      )
      .map(([phaseRow, { start, end }]) => {
        const phase = phaseRow as CircadianPhase;
        const targetEnergy = phaseEma?.scores?.[phase as keyof typeof phaseEma.scores]?.energy;

        return {
          phase,
          startTime: start,
          endTime: end,
          title: circadianPhaseInfo[phase as CircadianPhase].title,
          energy: targetEnergy,
        };
      })
      .sort((a, b) => dayjs(a.startTime).diff(dayjs(b.startTime)));

    return phaseEmaTimeline;
  }, [date, phaseEmasByDate, circadianPhaseBoundariesByDate]);

  const handleUpdatePhaseEma = (phase: CircadianPhase, energy: EnergyFeedbackType) => {
    updatePhaseEma({
      userId: user.userId,
      date,
      timezone: dayjs.tz.guess(),
      emaDatePhaseScore: {
        [phase]: {
          energy,
          timestamp: dayjs().format('YYYY-MM-DD HH:mm:ss'),
          timezone: dayjs.tz.guess(),
        },
      },
    });
  };

  return { phaseEmaTimeline, handleUpdatePhaseEma };
};
